import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { RefreshCw, Mail, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';

interface EmailVerificationInputProps {
  email: string;
  userType: number;
  onVerificationSuccess: () => void;
  onBackToLogin: () => void;
  onError: (error: string) => void;
}

const EmailVerificationInput: React.FC<EmailVerificationInputProps> = ({
  email,
  userType,
  onVerificationSuccess,
  onBackToLogin,
  onError
}) => {
  const { t } = useTranslation();
  const { refreshAuthState } = useAuth();
  const [verificationCode, setVerificationCode] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [verificationError, setVerificationError] = useState('');

  const handleSendCode = async () => {
    setIsSendingCode(true);
    setVerificationError('');
    
    try {
      await authService.resendVerification({ 
        email, 
        user_type: userType 
      });
      setCodeSent(true);
    } catch (error: any) {
      onError(error.message);
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      setVerificationError(t('auth.emailVerification.errors.invalidCode'));
      return;
    }

    setIsVerifying(true);
    setVerificationError('');
    
    try {
      const response = await authService.verifyEmail({
        email,
        code: verificationCode.trim()
      });
      
      // Refresh the authentication state to update the context
      refreshAuthState();
      
      onVerificationSuccess();
    } catch (error: any) {
      if (error.message.includes('Code incorrect') || error.message.includes('Invalid code')) {
        setVerificationError(t('auth.emailVerification.errors.invalidCode'));
      } else {
        setVerificationError(error.message);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6); // Only numbers, max 6 digits
    setVerificationCode(value);
    setVerificationError('');
  };

  return (
    <div className="bg-gradient-to-r from-brand-primary/5 to-brand-secondary/5 border border-brand-primary/20 rounded-xl p-4 mb-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 bg-brand-primary/10 rounded-lg flex items-center justify-center">
          <Mail className="h-4 w-4 text-brand-primary" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            {t('auth.emailVerification.title')}
          </h3>
          <p className="text-xs text-gray-600">
            {t('auth.emailVerification.subtitle')} <span className="font-medium text-brand-primary">{email}</span>
          </p>
        </div>
      </div>

      {/* Back to login button */}
      <button
        onClick={onBackToLogin}
        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 mb-3 transition-colors"
      >
        <ArrowLeft className="h-3 w-3" />
        <span>{t('auth.emailVerification.backToLogin')}</span>
      </button>

      {/* Send code button */}
      {!codeSent && (
        <div className="mb-3">
          <button
            onClick={handleSendCode}
            disabled={isSendingCode}
            className="inline-flex items-center gap-2 px-3 py-2 bg-brand-primary text-white text-xs font-medium rounded-lg hover:bg-brand-primary/90 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSendingCode ? (
              <>
                <RefreshCw className="h-3 w-3 animate-spin" />
                <span>{t('auth.emailVerification.sending')}</span>
              </>
            ) : (
              <>
                <RefreshCw className="h-3 w-3" />
                <span>{t('auth.emailVerification.resendButton')}</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Code sent confirmation */}
      {codeSent && (
        <div className="flex items-center gap-2 text-green-600 text-xs mb-3">
          <CheckCircle className="h-3 w-3" />
          <span>{t('auth.emailVerification.success.subtitle')}</span>
        </div>
      )}

      {/* Verification code input */}
      <div className="space-y-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            {t('auth.emailVerification.codeLabel')}
          </label>
          <input
            type="text"
            value={verificationCode}
            onChange={handleInputChange}
            placeholder={t('auth.emailVerification.codePlaceholder')}
            className={`w-full px-3 py-2 rounded-lg border transition-colors text-center text-sm font-mono tracking-wider ${
              verificationError
                ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:border-brand-primary focus:ring-brand-primary'
            } focus:outline-none focus:ring-1`}
            maxLength={6}
          />
          {verificationError && (
            <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {verificationError}
            </p>
          )}
        </div>

        {/* Verify button */}
        <button
          onClick={handleVerifyCode}
          disabled={isVerifying || !verificationCode.trim()}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-brand-primary to-brand-secondary text-white font-medium rounded-lg hover:from-brand-primary/90 hover:to-brand-secondary/90 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
        >
          {isVerifying ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span>{t('auth.emailVerification.verifying')}</span>
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4" />
              <span>{t('auth.emailVerification.verifyButton')}</span>
            </>
          )}
        </button>
      </div>

      {/* Help text */}
      <div className="mt-3 p-2 bg-gray-50 rounded-lg">
        <p className="text-xs text-gray-600">
          <strong>{t('auth.emailVerification.tips')}:</strong> {t('auth.emailVerification.checkSpam')} 
          {t('auth.emailVerification.codeExpires')}
        </p>
      </div>
    </div>
  );
};

export default EmailVerificationInput;
